import { registerPlugin } from '@capacitor/core';
import { isGoogleAndroidPlatform } from './googleAuth';
import type { AppSession } from '@/contexts/AuthContext';

const ACCESS_KEY = 'fm_access_token';
const REFRESH_KEY = 'fm_refresh_token';
const REFRESH_JTI_KEY = 'fm_refresh_jti';
const USER_KEY = 'fm_auth_user';

interface NativeSessionStorage {
  load(): Promise<{ value: string | null }>;
  save(options: { value: string }): Promise<void>;
  clear(): Promise<void>;
}
const nativeStore = registerPlugin<NativeSessionStorage>('SessionStorage');

function clearBrowserSlot() {
  for (const key of [ACCESS_KEY, REFRESH_KEY, REFRESH_JTI_KEY, USER_KEY]) localStorage.removeItem(key);
}

export async function loadPlatformSession(): Promise<AppSession | null> {
  if (isGoogleAndroidPlatform()) {
    // Old WebView storage must never become an Android credential fallback.
    clearBrowserSlot();
    const { value } = await nativeStore.load();
    if (!value) return null;
    try {
      const session = JSON.parse(value) as AppSession;
      if (session.access_token && session.refresh_token && session.user?.user_id) return session;
    } catch { /* Corrupt data is invalidated below. */ }
    await nativeStore.clear();
    return null;
  }
  const access_token = localStorage.getItem(ACCESS_KEY);
  const refresh_token = localStorage.getItem(REFRESH_KEY);
  const rawUser = localStorage.getItem(USER_KEY);
  if (!access_token || !refresh_token || !rawUser) return null;
  try {
    return { access_token, refresh_token, refresh_jti: localStorage.getItem(REFRESH_JTI_KEY) || undefined,
      user: JSON.parse(rawUser) as AppSession['user'] };
  } catch { return null; }
}

export async function savePlatformSession(session: AppSession): Promise<void> {
  if (isGoogleAndroidPlatform()) {
    clearBrowserSlot();
    await nativeStore.save({ value: JSON.stringify(session) });
    return;
  }
  localStorage.setItem(ACCESS_KEY, session.access_token);
  localStorage.setItem(REFRESH_KEY, session.refresh_token);
  if (session.refresh_jti) localStorage.setItem(REFRESH_JTI_KEY, session.refresh_jti);
  else localStorage.removeItem(REFRESH_JTI_KEY);
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
}

export async function clearPlatformSession(): Promise<void> {
  clearBrowserSlot();
  if (isGoogleAndroidPlatform()) await nativeStore.clear();
}
